// ----------------------------------------------------------------------------
// Difficulty grading
// ----------------------------------------------------------------------------
/**
 * The difficulty ratings an uploader can assign, ordered easiest → hardest.
 *
 * NOTE the DB column (`trl_image_assets.difficulty`) is a nullable free-form
 * TEXT with no CHECK constraint, so {@link TrlImageAsset.difficulty} stays a
 * loose `string`. These constants are the canonical *convention*: every lookup
 * must tolerate an absent or unrecognised value rather than assume membership.
 */
export const TRL_DIFFICULTIES = ['easy', 'medium', 'hard', 'very_hard', 'extreme'];
/** Human labels for the difficulties (admin UI + player map badges). */
export const TRL_DIFFICULTY_LABELS = {
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Hard',
    very_hard: 'Very Hard',
    extreme: 'Extreme',
};
/**
 * Colour-coded glyph per difficulty. Difficulty is conveyed by this emoji
 * everywhere it appears — there is no difficulty colour in the SCSS system, so
 * a badge only ever needs the generic `.badge` class plus one of these.
 */
export const TRL_DIFFICULTY_EMOJI = {
    easy: '🟢',
    medium: '🟡',
    hard: '🟠',
    very_hard: '🔴',
    extreme: '⚫',
};
/** Ordinal rank, for sorting a gallery by difficulty. */
export const TRL_DIFFICULTY_RANK = {
    easy: 0,
    medium: 1,
    hard: 2,
    very_hard: 3,
    extreme: 4,
};
/** Shown in place of a label when an image has no (or an unknown) difficulty. */
export const TRL_DIFFICULTY_UNRATED_LABEL = 'Unrated';
/** Shown in place of a glyph when an image has no (or an unknown) difficulty. */
export const TRL_DIFFICULTY_UNRATED_EMOJI = '⚪';
/** Narrow an untrusted difficulty string to a known rating, or undefined. */
export function asTrlDifficulty(raw) {
    return TRL_DIFFICULTIES.includes(raw) ? raw : undefined;
}
/** Display label for a possibly-missing/unknown difficulty. */
export function trlDifficultyLabel(raw) {
    const d = asTrlDifficulty(raw);
    return d ? TRL_DIFFICULTY_LABELS[d] : TRL_DIFFICULTY_UNRATED_LABEL;
}
/** Display glyph for a possibly-missing/unknown difficulty. */
export function trlDifficultyEmoji(raw) {
    const d = asTrlDifficulty(raw);
    return d ? TRL_DIFFICULTY_EMOJI[d] : TRL_DIFFICULTY_UNRATED_EMOJI;
}
/**
 * Strip the answer coordinates from a library image before sending to clients.
 */
export function toPublicTrlImageAsset(image) {
    return { id: image.id, url: image.url, name: image.name, area: image.area };
}
